import { isLegalSubdomain, getMainDomainUrl } from './legalSubdomain'

/**
 * Gets the appropriate asset URL based on the current hostname.
 *
 * - On main Achieve domains: Returns relative /assets/* URLs (uses Next.js rewrites)
 * - On legal subdomains: Returns absolute URLs pointing to main Achieve domain
 *
 * This function should be called in components that render asset links to ensure
 * the correct URL format is used based on where the page is being served.
 *
 * @param {string} url - The asset URL (can be Contentful URL or relative /assets/* URL)
 * @param {string} [hostname] - Optional hostname (defaults to window.location.hostname in browser)
 * @returns {string} The appropriate URL for the current domain
 *
 * @example
 * // On www.achieve.com:
 * getAssetUrl('https://assets.ctfassets.net/space/file.pdf')
 * // Returns: '/assets/files/space/file.pdf'
 *
 * @example
 * // On legal.achieve.com:
 * getAssetUrl('/assets/files/space/file.pdf')
 * // Returns: 'https://www.achieve.com/assets/files/space/file.pdf'
 */
export function getAssetUrl(url, hostname) {
  if (!url || typeof url !== 'string') return url

  // Get hostname from window if not provided (client-side)
  const currentHostname =
    hostname || (typeof window !== 'undefined' ? window.location.hostname : null)

  const isLegal = isLegalSubdomain(currentHostname)

  // First, normalize to relative /assets/* format
  let relativeUrl = url

  // If it's a Contentful URL, convert to relative
  if (
    url.startsWith('https://images.ctfassets.net/') ||
    url.startsWith('//images.ctfassets.net/')
  ) {
    relativeUrl = url
      .replace('https://images.ctfassets.net/', '/assets/images/')
      .replace('//images.ctfassets.net/', '/assets/images/')
  } else if (
    url.startsWith('https://assets.ctfassets.net/') ||
    url.startsWith('//assets.ctfassets.net/')
  ) {
    relativeUrl = url
      .replace('https://assets.ctfassets.net/', '/assets/files/')
      .replace('//assets.ctfassets.net/', '/assets/files/')
  } else {
    // Already relative or other URL
    relativeUrl = url
  }

  // If on legal subdomain, prepend main domain for the current environment
  if (isLegal && relativeUrl.startsWith('/assets/')) {
    const mainDomainUrl = getMainDomainUrl(currentHostname)
    return `${mainDomainUrl}${relativeUrl}`
  }

  // On main domain, return relative URL
  return relativeUrl
}
